// Keeps the attribute labels of the MDS map apart. Every label is written along its own axis line,
// lifted above it; two axes only a few degrees apart run almost parallel, and then one of the two
// labels sits in the strip between the lines, on top of the other. The fix works on the drawn text
// rather than on the angle: a pair whose text runs pass closer than a line of text is resolved by
// moving the label that sits between the two lines to the far side of its own line.

// A label's text is ~10px tall; closer than this, two runs of it touch.
export const LABEL_CLEARANCE = 12

const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y })
const dot = (a, b) => a.x * b.x + a.y * b.y
const cross = (a, b) => a.x * b.y - a.y * b.x

const pointToSegment = (p, a, b) => {
  const ab = sub(b, a)
  const len = dot(ab, ab)
  const t = 0 === len ? 0 : Math.max(0, Math.min(1, dot(sub(p, a), ab) / len))
  const q = { x: a.x + t * ab.x, y: a.y + t * ab.y }
  return Math.hypot(p.x - q.x, p.y - q.y)
}

const segmentsIntersect = (a0, a1, b0, b1) => {
  const d1 = cross(sub(a1, a0), sub(b0, a0))
  const d2 = cross(sub(a1, a0), sub(b1, a0))
  const d3 = cross(sub(b1, b0), sub(a0, b0))
  const d4 = cross(sub(b1, b0), sub(a1, b0))
  return d1 * d2 < 0 && d3 * d4 < 0
}

/** Shortest distance between two segments, 0 when they cross. */
export const segmentDistance = ([a0, a1], [b0, b1]) =>
  segmentsIntersect(a0, a1, b0, b1)
    ? 0
    : Math.min(
        pointToSegment(a0, b0, b1),
        pointToSegment(a1, b0, b1),
        pointToSegment(b0, a0, a1),
        pointToSegment(b1, a0, a1)
      )

/** Which side of the line through [p0, p1] a point lies on: 1, -1, or 0 on the line. */
export const sideOf = ([p0, p1], q) => Math.sign(cross(sub(p1, p0), sub(q, p0)))

const midpoint = ([a, b]) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })

/**
 * Decide which labels to move to the other side of their line.
 * @param {Array<{ run: [Point, Point], line: [Point, Point] }>} labels
 *   run = the drawn text's start and end, line = the axis line the text is written along
 * @returns {Set<number>} indexes of the labels to flip
 */
export const planLabelFlips = labels => {
  const flips = new Set()

  for (let i = 0; i < labels.length; i++) {
    for (let j = i + 1; j < labels.length; j++) {
      if (flips.has(i) || flips.has(j)) continue
      const a = labels[i]
      const b = labels[j]
      if (segmentDistance(a.run, b.run) >= LABEL_CLEARANCE) continue

      // a label sits between the two lines when the other line lies on the same side of its own
      // line as its text does
      const aBetween = sideOf(a.line, midpoint(a.run)) === sideOf(a.line, midpoint(b.line))
      const bBetween = sideOf(b.line, midpoint(b.run)) === sideOf(b.line, midpoint(a.line))

      if (aBetween) flips.add(i)
      else if (bBetween) flips.add(j)
    }
  }

  return flips
}

// A label normally sits 6px above its line; flipped, its 10px text hangs just below the line instead.
export const FLIPPED_DY = 12

// the axis path a label is written along; ids carry the axis end's coordinates, dots and signs
// included, so they are matched as strings rather than through a selector
const axisPathOf = (svgNode, textPath, pathClass) => {
  const id = (textPath?.getAttribute('href') || textPath?.getAttribute('xlink:href') || '').replace(/^#/, '')
  if (!id) return null
  for (const path of svgNode.querySelectorAll(`path.${pathClass}`)) {
    if (path.getAttribute('id') === id) return path
  }
  return null
}

/**
 * Measure the drawn attribute labels and move each one that crowds another to the far side of its
 * own line. Needs real text layout: where the text cannot report its glyph positions it does nothing.
 * @param svgNode root <svg>
 * @param {{ labelClass: string, pathClass: string }} classes
 */
export const separateAxisLabels = (svgNode, { labelClass, pathClass }) => {
  const labels = []
  const nodes = []

  for (const text of svgNode.querySelectorAll(`text.${labelClass}`)) {
    if ('function' !== typeof text.getNumberOfChars || 'function' !== typeof text.getStartPositionOfChar) continue
    const path = axisPathOf(svgNode, text.querySelector('textPath'), pathClass)
    if (!path || 'function' !== typeof path.getTotalLength) continue

    try {
      const count = text.getNumberOfChars()
      if (!count) continue
      const total = path.getTotalLength()
      labels.push({
        run: [text.getStartPositionOfChar(0), text.getEndPositionOfChar(count - 1)],
        line: [path.getPointAtLength(0), path.getPointAtLength(total)]
      })
      nodes.push(text)
    } catch {
      // a label not laid out (hidden, or the path has no length yet) takes no part
    }
  }

  for (const index of planLabelFlips(labels)) nodes[index].setAttribute('dy', FLIPPED_DY)
}
